import { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ApiError } from "@anyseal/api-client";
import { StatePanel } from "@anyseal/ui";
import { useAuth } from "../auth";
import { portalClient } from "../api";
import { useI18n } from "../i18n";
import { oauthFragment } from "../oauth";
import { AuthLayout } from "./AuthPages";

export function OAuthCompletePage() {
  const { locale } = useI18n();
  const { status, completeOAuth } = useAuth();
  const navigate = useNavigate();
  const started = useRef(false);
  const [error, setError] = useState<ApiError | Error | null>(null);
  const { flowToken, intent } = oauthFragment(location.hash);

  useEffect(() => {
    // Binding requires a session; anonymous users are sent to sign in (and
    // returned here afterwards) instead of facing a dead spinner.
    if (intent === "bind" && status === "anonymous") {
      navigate("/login", { state: { from: location.pathname + location.hash }, replace: true });
      return;
    }
    if (started.current || !flowToken || status === "initializing" || (intent === "bind" && status !== "authenticated")) return;
    started.current = true;
    const complete = intent === "bind"
      ? portalClient.post("/api/portal/v1/oauth/bind/complete", { flow_token: flowToken })
      : completeOAuth(flowToken);
    void complete.then(() => navigate(intent === "bind" ? "/dashboard/security" : "/dashboard", { replace: true })).catch((value: unknown) => setError(value instanceof Error ? value : new Error(locale === "zh-CN" ? "身份验证未完成，请重新发起。" : "Authentication did not complete. Start again.")));
  }, [completeOAuth, flowToken, intent, locale, navigate, status]);

  if (!flowToken) return <OAuthFrame><StatePanel kind="error" title={locale === "zh-CN" ? "登录链接无效" : "Invalid sign-in link"} description={locale === "zh-CN" ? "回调中缺少一次性登录凭据，请重新发起登录。" : "The callback is missing its one-time credential. Start sign-in again."} action={<Link to="/login">{locale === "zh-CN" ? "返回登录" : "Back to sign in"}</Link>} /></OAuthFrame>;
  if (error) return <OAuthFrame><StatePanel kind="error" title={locale === "zh-CN" ? "身份验证未完成" : "Authentication not completed"} description={error.message} requestId={error instanceof ApiError ? error.requestId : undefined} action={<Link to="/login">{locale === "zh-CN" ? "重新登录" : "Try again"}</Link>} /></OAuthFrame>;
  return <OAuthFrame><StatePanel kind="loading" title={intent === "bind" ? (locale === "zh-CN" ? "正在绑定身份" : "Linking identity") : (locale === "zh-CN" ? "正在安全登录" : "Completing secure sign-in")} description={locale === "zh-CN" ? "一次性凭据使用后会立即失效。" : "The one-time credential is invalidated immediately after use."} /></OAuthFrame>;
}

export function OAuthErrorPage() {
  const { locale } = useI18n();
  const code = new URLSearchParams(location.hash.slice(1)).get("code") ?? "invalid_flow";
  return <OAuthFrame><StatePanel kind="error" title={locale === "zh-CN" ? "身份提供商未授权" : "Identity provider did not authorize access"} description={`${locale === "zh-CN" ? "安全原因" : "Security reason"}: ${code}`} action={<Link to="/login">{locale === "zh-CN" ? "返回登录" : "Back to sign in"}</Link>} /></OAuthFrame>;
}

function OAuthFrame({ children }: { children: React.ReactNode }) {
  const { locale, setLocale } = useI18n();
  return <AuthLayout locale={locale} setLocale={setLocale}>{children}</AuthLayout>;
}
